import React, { useState } from 'react';
import { Button } from './ui/button';
import { Trophy, X, Check } from 'lucide-react';
import { i18n, tb } from '../utils/i18n';

interface WinnerCheckPopupProps {
  isOpen: boolean;
  onClose: () => void;
  nickname: string;
  rank: number | null;
  prizeAmount: number | null;
  hasParticipated?: boolean;
  onViewAllWinners?: () => void;
  onParticipate?: () => void;
  onDontShowAgain?: () => void;
}

const getTierLabel = (rank: number | null) => {
  if (!rank || rank <= 0) return '-';
  if (rank === 1) return i18n.t('winnerTier1');
  if (rank <= 5) return i18n.t('winnerTier2');
  if (rank <= 30) return i18n.t('winnerTier3');
  if (rank <= 130) return i18n.t('winnerTier4');
  return i18n.t('winnerTier5');
};

export function WinnerCheckPopup({ isOpen, onClose, nickname, rank, prizeAmount, hasParticipated, onViewAllWinners, onParticipate, onDontShowAgain }: WinnerCheckPopupProps) {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  if (!isOpen) return null;

  const tierLabel = getTierLabel(rank);
  const isWinner = rank !== null && prizeAmount !== null;
  const participated = Boolean(hasParticipated);
  const userLabel = `${nickname}${i18n.t('userSuffix')}`;

  const handleClose = () => {
    if (dontShowAgain && onDontShowAgain) {
      onDontShowAgain();
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md shadow-2xl transform transition-all duration-300">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center">
              <Trophy className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800 dark:text-white">{tb('winnerCheckTitle')}</h2>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <X className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          </button>
        </div>

        <div className="space-y-6">
          {!participated ? (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-6 text-center">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                <span className="font-bold text-lg">{userLabel}</span>
              </p>
              <div className="bg-white/70 dark:bg-gray-800/60 rounded-lg p-5 mb-4">
                <p className="text-gray-600 dark:text-gray-400 mb-2">{i18n.t('noParticipatedDesc')}</p>
                <p className="text-base font-semibold text-gray-700 dark:text-gray-300">{i18n.t('noParticipatedAction')}</p>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">{i18n.t('noParticipatedHint')}</p>
            </div>
          ) : isWinner && tierLabel !== '-' ? (
            <>
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-xl p-6 text-center">
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  <span className="font-bold text-lg text-orange-600 dark:text-orange-400">{userLabel}</span>
                  <br />
                  {i18n.t('winnerResultIntro')}
                </p>

                <div className="bg-white/60 dark:bg-gray-800/60 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    <Trophy className="w-6 h-6 text-yellow-500" />
                    <p className="text-3xl font-bold text-yellow-600">{tierLabel}</p>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{i18n.t('prizeLabel')}</p>
                  <p className="text-2xl font-bold text-orange-600">{prizeAmount.toLocaleString()} wHANDY</p>
                </div>

                <div className="bg-orange-100 dark:bg-orange-900/30 rounded-lg p-3">
                  <p className="text-sm text-orange-800 dark:text-orange-300 leading-relaxed">
                    {tb('winnerPrizeNotice')}
                  </p>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800/50 dark:to-gray-700/50 rounded-xl p-6 text-center">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                <span className="font-bold text-lg">{userLabel}</span>
              </p>
              <div className="bg-white/60 dark:bg-gray-800/60 rounded-lg p-6 mb-4">
                <p className="text-gray-600 dark:text-gray-400 mb-2">{i18n.t('notWinnerTitle')}</p>
                <p className="text-lg font-bold text-gray-700 dark:text-gray-300">{i18n.t('notWinnerDesc')}</p>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {i18n.t('notWinnerHint')}
              </p>
            </div>
          )}

          <div className="space-y-3">
            {onViewAllWinners && (
              <Button
                onClick={() => {
                  onViewAllWinners();
                  onClose();
                }}
                className="w-full h-12 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white font-semibold rounded-xl shadow-lg"
              >
                {i18n.t('viewAllWinnersButton')}
              </Button>
            )}

            {onParticipate && (
              <Button
                onClick={() => {
                  onParticipate();
                  onClose();
                }}
                className="w-full h-12 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold rounded-xl shadow-lg"
              >
                {i18n.t('joinBoom2Button')}
              </Button>
            )}

            <Button
              onClick={handleClose}
              className="w-full h-12 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white font-semibold rounded-xl shadow-lg"
            >
              {i18n.t('confirm')}
            </Button>
          </div>

          {onDontShowAgain && (
            <div className="flex items-center justify-center space-x-3 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                onClick={() => setDontShowAgain(!dontShowAgain)}
                variant="outline"
                className="flex-1 h-10 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl"
              >
                <Check className={`w-4 h-4 mr-2 ${dontShowAgain ? 'text-green-600' : ''}`} />
                {i18n.t('dontShowAgain')}
              </Button>
              <Button
                onClick={handleClose}
                variant="outline"
                className="flex-1 h-10 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl"
              >
                {i18n.t('close')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
